import { ChangeRequest, ChangeRequestStatus } from '../types/changeRequest';
import {
  ContactPreference,
  Gender,
  ProfessionalPath,
  Study,
  StudyDegreeType,
  Title,
  WorkSector,
} from '../types/member';
import { MemberFormData } from './memberForm';
import { enumMatches, tryNormalizeEnumValue } from './enumNormalize';
import { parseDateParts, toDateInputValue } from './dateUtils';
import type { ProfileSectionId } from './profileSectionCompleteness';

function isPending(cr: ChangeRequest): boolean {
  return enumMatches(ChangeRequestStatus, cr.status, ChangeRequestStatus.Pending);
}

export function latestPendingMemberChanges(requests: ChangeRequest[]): ChangeRequest[] {
  const latest = new Map<string, ChangeRequest>();
  for (const cr of requests.filter(isPending)) {
    const key = cr.fieldName.trim();
    const prev = latest.get(key);
    if (!prev || new Date(cr.requestedAt).getTime() > new Date(prev.requestedAt).getTime()) {
      latest.set(key, cr);
    }
  }
  return Array.from(latest.values()).sort(
    (a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime()
  );
}

function parseValue(raw?: string | null): unknown {
  if (raw == null || raw === '') return null;
  const trimmed = raw.trim();
  try {
    return JSON.parse(trimmed);
  } catch {
    return trimmed;
  }
}

function asString(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function asBool(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return Boolean(value);
}

function asYear(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function enumFormValue<T extends Record<string, string | number>>(
  enumObj: T,
  value: unknown
): string {
  const normalized = tryNormalizeEnumValue(enumObj, value);
  return normalized != null ? String(normalized) : asString(value);
}

const FIELD_TO_SECTION: Record<string, ProfileSectionId> = {
  Title: 'personal',
  Gender: 'personal',
  DateOfBirth: 'personal',
  Name: 'personal',
  Residential: 'personal',
  Phone: 'personal',
  Mobile: 'personal',
  Website: 'work',
  ContactPreference: 'personal',
  OrganisationName: 'work',
  ProfessionalPath: 'activities',
  IsGraduateStudent: 'activities',
  IsRetired: 'activities',
  ProfessionalTitle: 'work',
  WorkSector: 'work',
  ProfessionalInstitutionName: 'work',
  ProfessionalAddress: 'work',
  ProfessionalAddressEn: 'work',
  ProfessionalAddressGr: 'work',
  ProfessionalCity: 'work',
  ProfessionalCityEn: 'work',
  ProfessionalCityGr: 'work',
  ProfessionalPostalCode: 'work',
  ProfessionalEmail: 'work',
  ProfessionalPhone: 'work',
  IsVolunteer: 'work',
  Studies: 'studies',
  MainCommittee: 'committees',
  AdditionalCommittees: 'committees',
};

function pascalFieldName(fieldName: string): string {
  const trimmed = fieldName.trim();
  if (!trimmed) return trimmed;
  if (FIELD_TO_SECTION[trimmed]) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

export function pendingSectionId(fieldName: string): ProfileSectionId | null {
  const key = pascalFieldName(fieldName);
  return FIELD_TO_SECTION[key] ?? FIELD_TO_SECTION[fieldName.trim()] ?? null;
}

function mapStudies(value: unknown): Study[] {
  const list = Array.isArray(value) ? value : [];
  return list.map((item, i) => {
    const s = (item ?? {}) as Record<string, unknown>;
    return {
      id: String(s.id ?? i),
      titleEn: asString(s.titleEn),
      titleGr: asString(s.titleGr),
      degreeType:
        tryNormalizeEnumValue(StudyDegreeType, s.degreeType) ?? StudyDegreeType.Other,
      institutionEn: asString(s.institutionEn ?? s.institution),
      institutionGr: asString(s.institutionGr),
      isCurrent: asBool(s.isCurrent),
      startYear: asYear(s.startYear),
      duration: asYear(s.duration),
      completionYear: asYear(s.completionYear),
    };
  });
}

function applyField(form: MemberFormData, fieldName: string, rawNewValue?: string): void {
  const value = parseValue(rawNewValue);
  const record = asRecord(value);

  switch (fieldName) {
    case 'Phone':
      form.phone = asString(value);
      break;
    case 'Mobile':
      form.mobile = asString(value);
      break;
    case 'Website':
      form.website = asString(value);
      break;
    case 'Title':
      form.title = enumFormValue(Title, value);
      break;
    case 'Gender':
      form.gender = enumFormValue(Gender, value);
      break;
    case 'DateOfBirth': {
      const dateValue = toDateInputValue(asString(value));
      form.dateOfBirth = dateValue;
      const parts = parseDateParts(dateValue);
      form.dateOfBirthDay = parts.day;
      form.dateOfBirthMonth = parts.month;
      form.dateOfBirthYear = parts.year;
      break;
    }
    case 'ContactPreference':
      form.contactPreference = enumFormValue(ContactPreference, value);
      break;
    case 'Name':
      if (record) {
        form.firstNameEn = asString(record.firstNameEn);
        form.firstNameGr = asString(record.firstNameGr);
        form.lastNameEn = asString(record.lastNameEn);
        form.lastNameGr = asString(record.lastNameGr);
      }
      break;
    case 'Residential':
      if (record) {
        form.residentialAddressEn = asString(record.addressEn);
        form.residentialAddressGr = asString(record.addressGr);
        form.residentialCityEn = asString(record.cityEn);
        form.residentialCityGr = asString(record.cityGr);
        form.residentialPostalCode =
          asString(record.postalCodeEn) || asString(record.postalCodeGr);
        form.residentialCountryEn = asString(record.countryEn);
        form.residentialCountryGr = asString(record.countryGr);
      }
      break;
    case 'OrganisationName':
      if (record) {
        form.organisationNameEn = asString(record.en);
        form.organisationNameGr = asString(record.gr);
      }
      break;
    case 'ProfessionalPath':
      form.professionalPath = enumFormValue(ProfessionalPath, value);
      break;
    case 'IsGraduateStudent':
      form.isGraduateStudent = asBool(value);
      break;
    case 'IsRetired':
      form.isRetired = asBool(value);
      break;
    case 'IsVolunteer':
      form.isVolunteer = asBool(value);
      break;
    case 'ProfessionalTitle':
      if (record) {
        form.professionalTitleEn = asString(record.en);
        form.professionalTitleGr = asString(record.gr);
      }
      break;
    case 'ProfessionalInstitutionName':
      if (record) {
        form.professionalInstitutionNameEn = asString(record.en);
        form.professionalInstitutionNameGr = asString(record.gr);
      }
      break;
    case 'WorkSector':
      form.workSector = enumFormValue(WorkSector, value);
      break;
    case 'ProfessionalAddress':
    case 'ProfessionalAddressEn':
      form.professionalAddressEn = asString(value);
      break;
    case 'ProfessionalAddressGr':
      form.professionalAddressGr = asString(value);
      break;
    case 'ProfessionalCity':
    case 'ProfessionalCityEn':
      form.professionalCityEn = asString(value);
      break;
    case 'ProfessionalCityGr':
      form.professionalCityGr = asString(value);
      break;
    case 'ProfessionalPostalCode':
      form.professionalPostalCode = asString(value);
      break;
    case 'ProfessionalEmail':
      form.professionalEmail = asString(value);
      break;
    case 'ProfessionalPhone':
      form.professionalPhone = asString(value);
      break;
    case 'Studies':
      form.studies = mapStudies(value);
      break;
    case 'MainCommittee':
      form.mainCommittee = asString(value);
      break;
    case 'AdditionalCommittees':
      form.additionalCommittees = Array.isArray(value)
        ? value.map(v => asString(v)).filter(Boolean)
        : [];
      break;
    default:
      break;
  }
}

function syncActivitiesFromPendingFields(form: MemberFormData): void {
  const path = tryNormalizeEnumValue(ProfessionalPath, form.professionalPath);
  const working =
    path === ProfessionalPath.ActiveMuseumProfessional ||
    !!form.professionalInstitutionNameEn?.trim() ||
    !!form.professionalInstitutionNameGr?.trim();
  const studying =
    path === ProfessionalPath.Student ||
    form.isGraduateStudent ||
    (form.studies?.length ?? 0) > 0;
  form.professionalActivities = {
    working,
    studying,
    retired: form.isRetired,
  };
}

/** Overlay the latest pending change-request values onto the approved form. */
export function applyPendingMemberChanges(
  approvedForm: MemberFormData,
  requests: ChangeRequest[]
): MemberFormData {
  const next: MemberFormData = {
    ...approvedForm,
    professionalActivities: { ...approvedForm.professionalActivities },
    additionalCommittees: [...(approvedForm.additionalCommittees ?? [])],
    studies: (approvedForm.studies ?? []).map(s => ({ ...s })),
  };

  for (const cr of latestPendingMemberChanges(requests)) {
    applyField(next, pascalFieldName(cr.fieldName), cr.newValue);
  }
  syncActivitiesFromPendingFields(next);
  return next;
}

function sameFormValue(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (a == null || b == null) return a == null && b == null;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Score the approved profile. Pending values stay in the edit form, but they do not
 * raise completeness until approved. Edits that differ from that pending overlay are kept.
 */
export function formForApprovedCompleteness(
  current: MemberFormData,
  approved: MemberFormData,
  requests: ChangeRequest[]
): MemberFormData {
  if (latestPendingMemberChanges(requests).length === 0) {
    return current;
  }

  const overlaid = applyPendingMemberChanges(approved, requests);
  const result: MemberFormData = {
    ...approved,
    professionalActivities: { ...approved.professionalActivities },
    additionalCommittees: [...(approved.additionalCommittees ?? [])],
    studies: (approved.studies ?? []).map(s => ({ ...s })),
  };
  const draft = result as unknown as Record<string, unknown>;
  const source = current as unknown as Record<string, unknown>;
  const pendingView = overlaid as unknown as Record<string, unknown>;

  Object.keys(source).forEach(key => {
    if (!sameFormValue(source[key], pendingView[key])) {
      draft[key] = source[key];
    }
  });

  return result;
}
